import { useTranslation } from "@sagot/i18n";
import { Label, Select, toast } from "@sagot/ui";
import { useErrorMessage } from "../../../lib/api-errors";
import { useMembers } from "../../../roles/use-roles";
import { StageSelect } from "./lead-bits";
import type { LeadDetail, Stage } from "./lead-types";
import { usePatchLead } from "./use-leads";

const UNASSIGNED = "";

/** Stage + "Assigned to" selects in the lead sheet (`PATCH /v1/leads/{id}`). */
export function LeadControls({ lead, stages, canEdit }: { lead: LeadDetail; stages: readonly Stage[]; canEdit: boolean }) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const members = useMembers();
  const patch = usePatchLead();
  const active = (members.data ?? []).filter((m) => m.status === "active");
  const owners = active.some((m) => m.user_id === lead.owner_user_id) || !lead.owner_user_id ? active : [...active, { user_id: lead.owner_user_id, name: lead.owner_name ?? "" }];

  const onStage = (stageId: string) => {
    const stage = stages.find((s) => s.id === stageId);
    patch.mutate(
      { id: lead.id, patch: { stage_id: stageId } },
      {
        onSuccess: () => toast.success(t("engage.leads.sheet.stageUpdated", { stage: stage?.name ?? "" })),
        onError: (error) => toast.error(errorMessage(error)),
      },
    );
  };
  const onOwner = (userId: string) => {
    const name = owners.find((m) => m.user_id === userId)?.name ?? "";
    patch.mutate(
      { id: lead.id, patch: userId === UNASSIGNED ? { clear_owner: true } : { owner_user_id: userId } },
      {
        onSuccess: () => toast.success(userId === UNASSIGNED ? t("engage.leads.sheet.unassignedToast") : t("engage.leads.sheet.assigned", { name })),
        onError: (error) => toast.error(errorMessage(error)),
      },
    );
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label htmlFor="lead-stage">{t("engage.leads.sheet.stage")}</Label>
        <StageSelect id="lead-stage" stages={stages} value={lead.stage.id} disabled={!canEdit || patch.isPending} label={t("engage.leads.sheet.stage")} onChange={onStage} />
      </div>
      <div>
        <Label htmlFor="lead-owner">{t("engage.leads.sheet.owner")}</Label>
        <Select
          id="lead-owner"
          value={lead.owner_user_id ?? UNASSIGNED}
          disabled={!canEdit || patch.isPending}
          onChange={(event) => {
            onOwner(event.target.value);
          }}
        >
          {owners.map((member) => (
            <option key={member.user_id} value={member.user_id}>
              {member.name}
            </option>
          ))}
          <option value={UNASSIGNED}>{t("engage.leads.unassigned")}</option>
        </Select>
      </div>
    </div>
  );
}
